import { CustomFieldKind, formatLocationCustomFieldForExport } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';

import type { FieldValueFormatter } from '../Fields.types';

// Luxon's DATETIME_MED, spelled out: this package does not depend on luxon directly.
const DATETIME_MED = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' } as const;

export const formatFieldValue: FieldValueFormatter = (field, { currency, locale, timeFormat24 } = {}) => {
	if (field.kind === CustomFieldKind.customer) {
		// System `custody` arrives already unwrapped to its name; a custom customer field is the object.
		if (typeof field.value === 'string') return field.value;
		const customerValue = field.value as { name?: string } | undefined;
		return customerValue?.name ?? '';
	}

	if (field.kind === CustomFieldKind.location) {
		return formatLocationCustomFieldForExport(field.value, { locationHierarchyEnabled: true });
	}

	if (field.value == null || field.value === '') return '';

	switch (field.type) {
		case 'date':
			return DateTime.fromISOString(field.value as string).toFormat('D');
		case 'datetime':
			return DateTime.fromISOString(field.value as string).toDateTimeString({
				...DATETIME_MED,
				...(timeFormat24 != null && { hourCycle: timeFormat24 ? 'h23' : 'h12' }),
			});
		case 'currency':
			return new Intl.NumberFormat(locale, {
				style: 'currency',
				currency: currency ?? 'USD',
			}).format(Number(field.value));
		case 'number':
			return new Intl.NumberFormat(locale).format(Number(field.value));
		case 'multiple':
			return (field.value as string[]).join(', ');
		case 'boolean':
			return String(field.value);
		default:
			return String(field.value);
	}
};
